const STORAGE_KEY = "einsteinseeds-job-forum";

export const FORUM_TYPES = ["Job Referral", "Job Seeking"];
export const FORUM_PAGE_SIZE = 4;
export const COMMENTS_PAGE_SIZE = 6;

const defaultState = {
  posts: [],
  comments: [],
  likes: [],
};

export function loadForumState() {
  if (typeof window === "undefined") {
    return defaultState;
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);

  if (!raw) {
    return defaultState;
  }

  try {
    const parsed = JSON.parse(raw);
    return {
      posts: Array.isArray(parsed.posts) ? parsed.posts : defaultState.posts,
      comments: Array.isArray(parsed.comments)
        ? parsed.comments
        : defaultState.comments,
      likes: Array.isArray(parsed.likes) ? parsed.likes : defaultState.likes,
    };
  } catch (error) {
    return defaultState;
  }
}

export function saveForumState(nextState) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextState));
}

export function getForumTitle(post) {
  if (post.jobTitle) {
    return post.jobTitle;
  }

  const companyLabel =
    post.forumType === "Job Seeking"
      ? post.posterName || "Alumni Member"
      : post.companyName || "Opportunity";

  return `${post.forumType} • ${companyLabel}`;
}

export function formatForumDate(value) {
  return new Date(value).toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function formatForumDateTime(value) {
  return new Date(value).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function pickFirstValue(source, keys, fallback = "") {
  for (const key of keys) {
    if (source?.[key] !== undefined && source?.[key] !== null) {
      return source[key];
    }
  }
  return fallback;
}

export function normalizeJobForumPost(post) {
  const firstName = pickFirstValue(post, ["firstName", "FirstName"], "");
  const lastName = pickFirstValue(post, ["lastName", "LastName"], "");
  const fullName = `${firstName} ${lastName}`.trim();

  return {
    id: pickFirstValue(
      post,
      ["id", "Id", "postId", "PostId", "jobForumPostId", "JobForumPostId"],
      Date.now(),
    ),
    postedBy: Number(
      pickFirstValue(post, ["postedBy", "PostedBy"], 0),
    ),
    posterName: pickFirstValue(
      post,
      ["posterName", "PosterName", "postedByName", "PostedByName"],
      fullName || "Alumni Member",
    ),
    passedOutYear: pickFirstValue(
      post,
      ["passedOutYear", "PassedOutYear"],
      "Batch not set",
    ),
    forumType: pickFirstValue(post, ["forumType", "ForumType"], ""),
    jobTitle: pickFirstValue(post, ["jobTitle", "JobTitle"], ""),
    jobDescription: pickFirstValue(
      post,
      ["jobDescription", "JobDescription"],
      "",
    ),
    experience: Number(
      pickFirstValue(post, ["experience", "Experience"], 0),
    ),
    companyName: pickFirstValue(post, ["companyName", "CompanyName"], ""),
    locationCity: pickFirstValue(post, ["locationCity", "LocationCity"], ""),
    isActive: Boolean(pickFirstValue(post, ["isActive", "IsActive"], false)),
    contactNumber: pickFirstValue(
      post,
      ["contactNumber", "ContactNumber"],
      "",
    ),
    createdDate: pickFirstValue(
      post,
      ["createdDate", "CreatedDate", "createdAt", "CreatedAt"],
      new Date().toISOString(),
    ),
    updatedDate: pickFirstValue(
      post,
      ["updatedDate", "UpdatedDate", "updatedAt", "UpdatedAt"],
      new Date().toISOString(),
    ),
    likesCount: Number(
      pickFirstValue(post, ["likesCount", "LikesCount"], 0),
    ),
    commentsCount: Number(
      pickFirstValue(post, ["commentsCount", "CommentsCount"], 0),
    ),
    likedBy: Array.isArray(post?.likedBy) ? post.likedBy : [],
  };
}

export function normalizeJobForumComment(comment) {
  const firstName = pickFirstValue(
    comment,
    [
      "firstName",
      "FirstName",
      "commenterFirstName",
      "CommenterFirstName",
      "commentedByFirstName",
      "CommentedByFirstName",
    ],
    "",
  );
  const lastName = pickFirstValue(
    comment,
    [
      "lastName",
      "LastName",
      "commenterLastName",
      "CommenterLastName",
      "commentedByLastName",
      "CommentedByLastName",
    ],
    "",
  );
  const fullName = `${firstName} ${lastName}`.trim();

  return {
    id: pickFirstValue(
      comment,
      ["id", "Id", "commentId", "CommentId"],
      Date.now(),
    ),
    postId: Number(
      pickFirstValue(
        comment,
        ["postId", "PostId", "jobForumPostId", "JobForumPostId"],
        0,
      ),
    ),
    postedBy: Number(
      pickFirstValue(
        comment,
        ["postedBy", "PostedBy", "commentedBy", "CommentedBy"],
        0,
      ),
    ),
    posterName: pickFirstValue(
      comment,
      [
        "posterName",
        "PosterName",
        "commentedByName",
        "CommentedByName",
        "commenterName",
        "CommenterName",
      ],
      fullName || "Alumni Member",
    ),
    passedOutYear: pickFirstValue(
      comment,
      [
        "passedOutYear",
        "PassedOutYear",
        "commenterPassedOutYear",
        "CommenterPassedOutYear",
        "commentedByPassedOutYear",
        "CommentedByPassedOutYear",
      ],
      "Batch not set",
    ),
    content: pickFirstValue(
      comment,
      ["content", "Content", "commentText", "CommentText"],
      "",
    ),
    createdDate: pickFirstValue(
      comment,
      ["createdDate", "CreatedDate", "createdAt", "CreatedAt"],
      new Date().toISOString(),
    ),
    updatedDate: pickFirstValue(
      comment,
      ["updatedDate", "UpdatedDate", "updatedAt", "UpdatedAt"],
      new Date().toISOString(),
    ),
    isActive: Boolean(pickFirstValue(comment, ["isActive", "IsActive"], true)),
  };
}

export function normalizeJobForumLike(like) {
  return {
    id: pickFirstValue(like, ["id", "Id", "likeId", "LikeId"], Date.now()),
    postId: Number(
      pickFirstValue(
        like,
        ["postId", "PostId", "jobForumPostId", "JobForumPostId"],
        0,
      ),
    ),
    likedBy: Number(
      pickFirstValue(like, ["likedBy", "LikedBy"], 0),
    ),
  };
}
